import type { CSSProperties, ReactElement } from 'react';
import React, { useId } from 'react';
import styled, { css } from 'styled-components';

import FocusOutlineStyle from '@rover/kibble/styles/FocusOutlineStyle';

type LabelProps = {
  isSelected?: boolean;
  disabled?: boolean;
  styleHover?: Record<string, string | undefined>;
};

const getActiveAndHoverStyle: Parameters<typeof styled.label<LabelProps>>[number] = ({
  styleHover,
  theme,
}) => {
  return css`
    border-color: ${theme.colors.interactive_border.primary_pressed};
    color: ${theme.colors.text.secondary};
    ${styleHover && css(styleHover)}
  `;
};

export const Label = styled.label<LabelProps>`
  display: flex;
  min-height: ${({ theme }) => theme.space['8x']};
  max-width: 100%;
  margin: ${({ theme }) => `0 ${theme.space['2x']} ${theme.space['2x']} 0`};
  border-radius: ${({ theme }) => theme.radii.round};
  background: ${({ disabled, theme }) =>
    disabled ? theme.background.secondary : theme.background.primary};
  font-size: ${({ theme }) => theme.text['100'].fontSize};
  font-weight: 400;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  position: relative;
  overflow-wrap: break-word;
  cursor: pointer;
  user-select: none;
  padding: ${({ theme }) => `${theme.space['1x']} ${theme.space['4x']}`};
  border: 2px solid ${({ theme }) => theme.colors.border.primary};
  color: ${({ theme }) => theme.colors.text.tertiary};
  &:not(:disabled) {
    @media (hover: hover) {
      &:hover {
        ${getActiveAndHoverStyle}
      }
    }
    &:active {
      ${getActiveAndHoverStyle}
    }
  }

  ${({ isSelected, styleHover, theme }) =>
    !isSelected
      ? ''
      : css`
          border: 2px solid ${theme.colors.interactive_border.primary_active};
          color: ${theme.colors.interactive_text.primary_base};
          @media (hover: hover) {
            &:hover {
              ${css`
                /* && makes the styles more specific and higher in priority won't beat inline styles */
                && {
                  ${styleHover
                    ? css(styleHover)
                    : css`
                        border-color: ${theme.colors.interactive_border
                          .primary_active_pressed} !important;
                      `}
                }
              `}
            }
          }
        `}
`;
// Since we want a focus outline on the pill button only when selected via keyboard (not via click),
// we need to use :focus-visible on the checkbox.  However, this doesn't allow us to use :focus-within
// on the "label".  Hence this hacky element which is selectable as a sibling of the checkbox.
const CheckboxFocus = styled.div``;
const HiddenCheckbox = styled.input`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
  &:focus-visible ~ ${CheckboxFocus} {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    ${FocusOutlineStyle({
      outlineOffset: 2,
    })};
  }
`;
export type Props = {
  onClick: (value: string) => void;
  allowSelectMultiple?: boolean;
  inputGroupName?: string;
  isSelected: boolean;
  disabled?: boolean;
  label: string;
  value?: string;
  style?: CSSProperties;
  styleHover?: Record<string, string | undefined>;
};

const SelectPill = (props: Props): ReactElement => {
  const {
    onClick,
    allowSelectMultiple = true,
    inputGroupName,
    isSelected = false,
    label,
    value,
    disabled,
    style,
    styleHover,
  } = props;
  const pillId = useId();
  return (
    <Label
      isSelected={isSelected}
      disabled={disabled}
      htmlFor={pillId}
      data-testid="styled-label"
      style={style}
      styleHover={styleHover}
    >
      {label}
      <HiddenCheckbox
        type={allowSelectMultiple ? 'checkbox' : 'radio'}
        name={inputGroupName}
        id={pillId}
        checked={isSelected}
        disabled={disabled}
        onChange={() => onClick(value ?? label)}
        data-testid="styled-input"
      />
      <CheckboxFocus />
    </Label>
  );
};

export default SelectPill;
